import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  InputAdornment,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  ContactPage as ContactIcon,
  MenuBook as MenuBookIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  LockReset as LockResetIcon,
  VisibilityOff as VisibilityOffIcon,
  PhonelinkErase as Disable2FAIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  Security as SecurityIcon,
  Badge as BadgeIcon,
  GppGood as PrimaryRegIcon
} from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { adminAPI } from '../../services/api';

// Defined at module level so it is never unmounted when parent state changes
const AddModuleForm = ({ codEtu, onSuccess }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [allModules, setAllModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [year, setYear]           = useState(String(new Date().getFullYear()));
  const [group, setGroup]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [msg, setMsg]             = useState(null);
  const years = [0, 1, 2, 3].map(i => String(new Date().getFullYear() - i));

  // Load all modules once when the panel opens (same source as ExamPlanning)
  const handleOpen = async () => {
    setPanelOpen(o => !o);
    setMsg(null);
    if (allModules.length > 0) return; // already loaded
    setModulesLoading(true);
    try {
      const stats = await adminAPI.getDetailedStats();
      const map = new Map();
      (Array.isArray(stats) ? stats : []).forEach(item => {
        if (item.cod_elp && !map.has(item.cod_elp)) {
          map.set(item.cod_elp, { cod_elp: item.cod_elp, lib_elp: item.lib_elp || item.cod_elp });
        }
      });
      setAllModules(Array.from(map.values()).sort((a, b) => (a.lib_elp || '').localeCompare(b.lib_elp || '')));
    } catch (_) {}
    setModulesLoading(false);
  };

  const handleSelect = async (_, module, reason) => {
    // Only fetch the group when the user actually commits a selection (click or keyboard Enter)
    if (reason !== 'selectOption') return;
    setSelected(module);
    setGroup(null);
    if (!module) return;
    try {
      const preview = await adminAPI.getModuleGroupPreview(codEtu, module.cod_elp);
      setGroup(preview.group_name || null);
    } catch (_) {}
  };

  const handleAdd = async () => {
    if (!selected) return;
    setLoading(true); setMsg(null);
    try {
      const result = await adminAPI.addStudentModule(codEtu, { cod_elp: selected.cod_elp, cod_anu: year });
      const grp = result.group_name || group;
      setMsg({ type: 'success', text: `Module ${selected.cod_elp} ajouté${grp ? ` — Groupe : ${grp}` : ''}.` });
      setSelected(null); setGroup(null);
      await onSuccess();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Erreur lors de l\'ajout.' });
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Button size="small" variant="outlined" startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{ borderRadius: 2, borderColor: '#2c3e50', color: '#2c3e50', '&:hover': { bgcolor: '#f1f5f9' } }}
      >
        Ajouter un module manuellement
      </Button>

      {panelOpen && (
        <Paper elevation={0} sx={{ mt: 1.5, p: 2, border: '1.5px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
          {msg && <Alert severity={msg.type} sx={{ mb: 1.5 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flex: 1, minWidth: 260 }}
              options={allModules}
              loading={modulesLoading}
              getOptionLabel={opt => `${opt.lib_elp || opt.cod_elp} (${opt.cod_elp})`}
              isOptionEqualToValue={(o, v) => o.cod_elp === v?.cod_elp}
              value={selected}
              onChange={handleSelect}
              renderOption={(props, o) => (
                <li {...props} key={o.cod_elp}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{o.lib_elp || o.cod_elp}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>{o.cod_elp}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Module" size="small"
                  InputProps={{ ...params.InputProps, endAdornment: (
                    <>{modulesLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
                  )}}
                />
              )}
              noOptionsText="Aucun module trouvé"
            />
            <Select size="small" value={year} onChange={e => setYear(e.target.value)} sx={{ minWidth: 120 }}>
              {years.map(y => <MenuItem key={y} value={y}>{parseInt(y) - 1}/{y}</MenuItem>)}
            </Select>
            <Button variant="contained" size="small" onClick={handleAdd}
              disabled={!selected || loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              sx={{ height: 40, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' }, '&:disabled': { bgcolor: '#94a3b8' } }}
            >
              Ajouter
            </Button>
          </Box>
          {selected && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Groupe répartition :</Typography>
              {group
                ? <Chip label={group} size="small" color="primary" sx={{ fontWeight: 700, fontSize: '0.75rem' }} />
                : <Typography variant="caption" color="text.disabled">Aucune règle trouvée</Typography>
              }
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [situationData, setSituationData] = useState({ administrative: [], pedagogical: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20
  });
  
  // Search and filter state
  const [filters, setFilters] = useState({
    search: '',
    program: '',
    year: '',
    page: 1,
    limit: 20
  });

  const [programs, setPrograms] = useState([]);
  const [years, setYears] = useState([]);

  // Password reset state
  const [pwdDialog, setPwdDialog] = useState({ open: false, codEtu: '', name: '' });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdSuccess, setPwdSuccess] = useState(null);
  const [pwdError, setPwdError] = useState(null);

  // Disable 2FA state
  const [disable2FADialog, setDisable2FADialog] = useState({ open: false, codEtu: '', name: '' });
  const [disable2FALoading, setDisable2FALoading] = useState(false);
  const [disable2FAError, setDisable2FAError] = useState(null);

  // Block / unblock login state
  const [blockDialog, setBlockDialog] = useState({ open: false, codEtu: '', name: '', isBlocked: false });
  const [blockMessage, setBlockMessage] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockError, setBlockError] = useState(null);


  // Load students data
  const loadStudents = async (newFilters = filters) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await adminAPI.searchStudents(newFilters);
      setStudents(response.students);
      setPagination(response.pagination);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load students');
      console.error('Error loading students:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data and options
  useEffect(() => {
    loadStudents();
    loadFilterOptions();
  }, []);

  // Load filter options (programs and years)
  const loadFilterOptions = async () => {
    try {
      const overviewData = await adminAPI.getDataOverview();
      
      // Extract unique programs and years
      const uniquePrograms = [...new Set(overviewData.students_by_program?.map(p => p.lib_etp) || [])];
      const uniqueYears = overviewData.students_by_year?.map(y => y.cod_anu) || [];
      
      setPrograms(uniquePrograms);
      setYears(uniqueYears);
      
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  // Handle search
  const handleSearch = () => {
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    loadStudents(newFilters);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value, page: 1 };
    setFilters(newFilters);
    if (field !== 'search') {
      loadStudents(newFilters);
    }
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      program: '',
      year: '',
      page: 1,
      limit: 20
    };
    setFilters(clearedFilters);
    loadStudents(clearedFilters);
  };

  // Handle pagination
  const handlePageChange = (event, page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadStudents(newFilters);
  };

  // NEW: Handle Export
const handleExport = async () => {
    try {
      setIsLoading(true);
      // Ensure we use current filter state
      await adminAPI.exportStudents({
        search: filters.search,
        program: filters.program,
        year: filters.year
      });
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export student data');
    } finally {
      setIsLoading(false);
    }
  };

  // Open password reset dialog
  const openPwdDialog = (student) => {
    setPwdDialog({ open: true, codEtu: student.cod_etu, name: student.full_name });
    setNewPassword('');
    setShowPassword(false);
    setPwdError(null);
    setPwdSuccess(null);
  };

  const handleResetPassword = async () => {
    if (newPassword && newPassword.length < 6) {
      setPwdError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    setPwdLoading(true);
    setPwdError(null);
    try {
      await adminAPI.resetStudentPassword(pwdDialog.codEtu, newPassword || null);
      setPwdSuccess(newPassword
        ? 'Mot de passe modifié avec succès.'
        : `Mot de passe réinitialisé au code étudiant (${pwdDialog.codEtu}).`
      );
      setNewPassword('');
    } catch (e) {
      setPwdError(e.response?.data?.error || 'Erreur lors de la modification.');
    } finally {
      setPwdLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setDisable2FALoading(true);
    setDisable2FAError(null);
    try {
      await adminAPI.disableStudent2FA(disable2FADialog.codEtu);
      setDisable2FADialog({ open: false, codEtu: '', name: '' });
      // Refresh list so the badge disappears
      loadStudents();
    } catch (e) {
      setDisable2FAError(e.response?.data?.error || 'Erreur lors de la désactivation.');
    } finally {
      setDisable2FALoading(false);
    }
  };

  // Open block/unblock dialog
  const openBlockDialog = (student) => {
    setBlockDialog({ open: true, codEtu: student.cod_etu, name: student.full_name, isBlocked: !!student.is_blocked });
    setBlockMessage(student.block_message || '');
    setBlockError(null);
  };

  const handleBlockToggle = async () => {
    if (!blockDialog.isBlocked && !blockMessage.trim()) {
      setBlockError('Veuillez saisir un message à afficher à l\'étudiant.');
      return;
    }
    setBlockLoading(true);
    setBlockError(null);
    try {
      if (blockDialog.isBlocked) {
        await adminAPI.unblockStudent(blockDialog.codEtu);
      } else {
        await adminAPI.blockStudent(blockDialog.codEtu, blockMessage);
      }
      setBlockDialog({ open: false, codEtu: '', name: '', isBlocked: false });
      loadStudents();
    } catch (e) {
      setBlockError(e.response?.data?.error || 'Erreur lors de l\'opération.');
    } finally {
      setBlockLoading(false);
    }
  };

  // View student details
  const viewStudentDetails = async (studentId) => {
    try {
      setIsLoading(true);
      setSituationData({ administrative: [], pedagogical: [] });
      const response = await adminAPI.getStudent(studentId);
      setSelectedStudent(response);
      setTabValue(0);
      setDialogOpen(true);
      // Load situation in parallel (non-blocking)
      adminAPI.getStudentSituation(response.student.cod_etu)
        .then(d => setSituationData(d))
        .catch(() => {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load student details');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  // Get sync status color
  const getSyncStatusColor = (syncStatus) => {
    switch (syncStatus) {
      case 'recent': return 'success';
      case 'week': return 'warning';
      default: return 'default';
    }
  };

  // Get grade color
  const getGradeColor = (grade) => {
    if (grade === null || grade === undefined) return 'default';
    const numGrade = parseFloat(grade);
    if (numGrade >= 16) return 'success';
    if (numGrade >= 14) return 'info';
    if (numGrade >= 12) return 'primary';
    if (numGrade >= 10) return 'warning';
    return 'error';
  };

  // Calculate academic progress
  const calculateProgress = (grades) => {
    if (!grades || grades.length === 0) return { total: 0, passed: 0, failed: 0, average: 0 };
    
    const validGrades = grades.filter(g => g.not_elp !== null && g.not_elp !== undefined);
    const total = validGrades.length;
    const passed = validGrades.filter(g => parseFloat(g.not_elp) >= 10).length;
    const failed = total - passed;
    const sum = validGrades.reduce((acc, g) => acc + parseFloat(g.not_elp), 0);
    const average = total > 0 ? (sum / total).toFixed(2) : 0;
    
    return { total, passed, failed, average };
  };

  // Student Details Dialog — mirrors what the student sees
  const StudentDetailsDialog = () => {
    if (!selectedStudent) return null;
    const { student, grades } = selectedStudent;
    const { administrative, pedagogical } = situationData;

    // ── Profil tab ───────────────────────────────────────────────
    const ProfilTab = () => {
      const rows = [
        ['Code Apogée', student.cod_etu],
        ['Nom', `${student.lib_nom_pat_ind || ''} ${student.lib_pr1_ind || ''}`.trim()],
        ['Nom arabe', student.lib_nom_ind_arb ? `${student.lib_nom_ind_arb} ${student.lib_prn_ind_arb || ''}`.trim() : null],
        ['CIN', student.cin_ind || '—'],
        ['CNE', student.cod_nne_ind || '—'],
        ['Date de naissance', formatDate(student.date_nai_ind)],
        ['Lieu de naissance', student.lib_vil_nai_etu || '—'],
        ['Sexe', student.cod_sex_etu === 'M' ? 'Masculin' : student.cod_sex_etu === 'F' ? 'Féminin' : '—'],
        ['Filière', student.lib_etp || '—'],
        ['Année universitaire', student.cod_anu || '—'],
      ].filter(([, v]) => v);
      return (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Avatar
            src={adminAPI.getStudentPhotoUrl(student.cod_etu)}
            sx={{ width: 100, height: 100, bgcolor: '#2c3e50', fontSize: '2rem', flexShrink: 0, mt: 0.5 }}
          >
            {student.lib_nom_pat_ind?.charAt(0)}
          </Avatar>
          <Paper elevation={0} sx={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', minWidth: 300 }}>
            <Table size="small">
              <TableBody>
                {rows.map(([label, value]) => (
                  <TableRow key={label} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', width: 170, borderRight: '1px solid #e2e8f0', py: 1.2 }}>{label}</TableCell>
                    <TableCell sx={{ py: 1.2, direction: label === 'Nom arabe' ? 'rtl' : 'ltr' }}>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      );
    };

    // ── Situation Administrative tab ─────────────────────────────
    const AdminSitTab = () => {
      if (!administrative.length) return (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
          <SchoolIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>Aucune situation administrative disponible</Typography>
        </Box>
      );
      return (
        <Paper elevation={0} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Année', 'Filière', 'Inscriptions', 'Type', 'Statut Diplôme', 'Enregistré par'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {administrative.map((row, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f0f4ff' } }}>
                  <TableCell sx={{ py: 1.2 }}>
                    <Chip label={row.cod_anu} size="small" sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                  </TableCell>
                  <TableCell sx={{ py: 1.2, maxWidth: 240 }}>
                    <Typography variant="body2" fontWeight={600}>{row.lib_etp || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {row.cod_etp || '—'}{row.cod_vrs_vet ? ` · v${row.cod_vrs_vet}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.nbr_ins_cyc != null && <Chip label={`Cycle: ${row.nbr_ins_cyc}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20 }} />}
                      {row.nbr_ins_etp != null && <Chip label={`Filière: ${row.nbr_ins_etp}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20 }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Tooltip title={row.tem_iae_prm === 'O' ? 'Inscription principale de cette année' : 'Inscription secondaire (double inscription)'}>
                      <Chip
                        icon={row.tem_iae_prm === 'O' ? <PrimaryRegIcon sx={{ fontSize: 14 }} /> : undefined}
                        label={row.tem_iae_prm === 'O' ? 'Principale' : 'Secondaire'}
                        size="small"
                        variant="outlined"
                        color={row.tem_iae_prm === 'O' ? 'primary' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.68rem', height: 22 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Chip
                      label={row.tem_dip_iae === 'O' ? 'Diplômé' : 'En cours'}
                      size="small"
                      color={row.tem_dip_iae === 'O' ? 'success' : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    {row.cod_uti ? (
                      <Tooltip title={`Inscription créée le ${formatDate(row.dat_cre_iae)}${row.dat_mod_iae ? ` · modifiée le ${formatDate(row.dat_mod_iae)}` : ''}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <BadgeIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>{row.cod_uti}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>{formatDate(row.dat_cre_iae)}</Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      );
    };

    // ── Situation Pédagogique tab ────────────────────────────────
    const PedagoSitTab = () => {
      const [selected, setSelected] = React.useState(new Set());
      const [deleting, setDeleting] = React.useState(false);
      const [deleteMsg, setDeleteMsg] = React.useState(null);

      const refreshSituation = async () => {
        const d = await adminAPI.getStudentSituation(student.cod_etu);
        setSituationData(d);
        setSelected(new Set());
      };

      const rowKey = (m) => `${m.cod_elp}|${m.cod_anu}`;

      const toggleRow = (m) => {
        setSelected(prev => {
          const next = new Set(prev);
          next.has(rowKey(m)) ? next.delete(rowKey(m)) : next.add(rowKey(m));
          return next;
        });
      };

      const toggleAll = (rows) => {
        const keys = rows.map(rowKey);
        const allChecked = keys.every(k => selected.has(k));
        setSelected(prev => {
          const next = new Set(prev);
          allChecked ? keys.forEach(k => next.delete(k)) : keys.forEach(k => next.add(k));
          return next;
        });
      };

      const handleDelete = async () => {
        const toDelete = pedagogical
          .filter(m => selected.has(rowKey(m)))
          .map(m => ({ cod_elp: m.cod_elp, cod_anu: m.cod_anu }));
        if (!toDelete.length) return;
        setDeleting(true); setDeleteMsg(null);
        try {
          await adminAPI.removeStudentModules(student.cod_etu, toDelete);
          setDeleteMsg({ type: 'success', text: `${toDelete.length} module(s) supprimé(s).` });
          await refreshSituation();
        } catch (e) {
          setDeleteMsg({ type: 'error', text: e.response?.data?.error || 'Erreur lors de la suppression.' });
        } finally { setDeleting(false); }
      };

      const semColors = { 1:'#2563eb',2:'#d97706',3:'#16a34a',4:'#7c3aed',5:'#dc2626',6:'#0891b2' };

      if (!pedagogical.length) return (
        <Box>
          <AddModuleForm key={student.cod_etu} codEtu={student.cod_etu} onSuccess={refreshSituation} />
          <Box sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>
            <MenuBookIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography>Aucune situation pédagogique disponible</Typography>
          </Box>
        </Box>
      );

      // Group by year
      const byYear = pedagogical.reduce((acc, m) => {
        const y = m.cod_anu || '?';
        if (!acc[y]) acc[y] = [];
        acc[y].push(m);
        return acc;
      }, {});

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AddModuleForm key={student.cod_etu} codEtu={student.cod_etu} onSuccess={refreshSituation} />

          {/* Delete toolbar */}
          {deleteMsg && (
            <Alert severity={deleteMsg.type} onClose={() => setDeleteMsg(null)} sx={{ borderRadius: 2 }}>
              {deleteMsg.text}
            </Alert>
          )}
          {selected.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {selected.size} module(s) sélectionné(s)
              </Typography>
              <Button
                size="small" variant="contained" color="error"
                startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                disabled={deleting}
                onClick={handleDelete}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Supprimer la sélection
              </Button>
              <Button size="small" variant="text" onClick={() => setSelected(new Set())} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Désélectionner
              </Button>
            </Box>
          )}

          {Object.keys(byYear).sort((a,b) => b-a).map(year => {
            const rows = byYear[year];
            const yearKeys = rows.map(rowKey);
            const allChecked = yearKeys.every(k => selected.has(k));
            const someChecked = yearKeys.some(k => selected.has(k));
            return (
              <Paper key={year} elevation={0} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.2, bgcolor: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={someChecked && !allChecked}
                      onChange={() => toggleAll(rows)}
                      sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' }, p: 0.5 }}
                    />
                    <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.9rem' }}>
                      Année universitaire {parseInt(year)-1}–{year}
                    </Typography>
                  </Box>
                  <Chip label={`${rows.length} module(s)`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '0.7rem' }} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell padding="checkbox" sx={{ width: 40 }} />
                      {['Sem', 'Module', 'Intitulé arabe', 'Statut'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#475569', py: 1 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((m, i) => {
                      const sc = semColors[m.semester_number] || '#64748b';
                      const isChecked = selected.has(rowKey(m));
                      return (
                        <TableRow
                          key={i}
                          selected={isChecked}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, '&.Mui-selected': { bgcolor: '#eff6ff' }, '&.Mui-selected:hover': { bgcolor: '#dbeafe' } }}
                          onClick={() => toggleRow(m)}
                        >
                          <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                            <Checkbox size="small" checked={isChecked} onChange={() => toggleRow(m)} sx={{ p: 0.5 }} />
                          </TableCell>
                          <TableCell sx={{ py: 1, width: 48 }}>
                            {m.semester_number ? (
                              <Chip label={`S${m.semester_number}`} size="small" sx={{ fontWeight: 800, fontSize: '0.7rem', height: 20, bgcolor: sc+'18', color: sc, border: `1px solid ${sc}44` }} />
                            ) : '—'}
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{m.lib_elp || m.cod_elp}</Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>{m.cod_elp}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1, direction: 'rtl', maxWidth: 220 }}>
                            <Typography variant="body2" sx={{ color: '#334155', fontFamily: 'Arial', lineHeight: 1.3 }}>{m.lib_elp_arb || '—'}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              label={m.eta_iae === 'E' ? 'Inscrit' : m.eta_iae || '—'}
                              size="small"
                              color={m.eta_iae === 'E' ? 'success' : 'default'}
                              sx={{ fontWeight: 600, fontSize: '0.68rem', height: 20 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            );
          })}
        </Box>
      );
    };

    // ── Notes & Résultats tab ────────────────────────────────────
    const NotesTab = () => {
      if (!grades || !grades.length) return (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
          <AssessmentIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>Aucune note enregistrée</Typography>
        </Box>
      );
      // Group by year + session
      const byPeriod = grades.reduce((acc, g) => {
        const key = `${g.cod_anu}|${g.cod_ses}`;
        if (!acc[key]) acc[key] = { cod_anu: g.cod_anu, cod_ses: g.cod_ses, rows: [] };
        acc[key].rows.push(g);
        return acc;
      }, {});
      const gradeColor = (v) => { if (v == null) return '#64748b'; const n = parseFloat(v); if (n >= 14) return '#16a34a'; if (n >= 10) return '#d97706'; return '#dc2626'; };
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.values(byPeriod).sort((a,b) => b.cod_anu - a.cod_anu || a.cod_ses.localeCompare(b.cod_ses)).map(period => {
            const passed = period.rows.filter(g => g.not_elp != null && parseFloat(g.not_elp) >= 10).length;
            const total  = period.rows.filter(g => g.not_elp != null).length;
            const avg    = total ? (period.rows.filter(g => g.not_elp != null).reduce((s,g) => s + parseFloat(g.not_elp), 0) / total).toFixed(2) : null;
            return (
              <Paper key={`${period.cod_anu}|${period.cod_ses}`} elevation={0} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.2, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.9rem' }}>
                    {period.cod_anu} — Session {period.cod_ses}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {avg && <Chip label={`Moy: ${avg}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />}
                    <Chip label={`${passed}/${total} validés`} size="small" sx={{ bgcolor: passed === total ? '#16a34a33' : '#dc262633', color: 'white', fontSize: '0.7rem' }} />
                  </Box>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {['Module', 'Intitulé', 'Note', 'Résultat'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#475569', py: 1 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {period.rows.sort((a,b) => (a.semester_number||99) - (b.semester_number||99) || (a.lib_elp||'').localeCompare(b.lib_elp||'')).map((g, i) => (
                      <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ py: 1, fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b', width: 110 }}>{g.cod_elp}</TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={500}>{g.lib_elp || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, width: 70 }}>
                          {g.not_elp != null ? (
                            <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: gradeColor(g.not_elp) }}>
                              {parseFloat(g.not_elp).toFixed(2)}
                            </Typography>
                          ) : <Typography variant="caption" color="text.disabled">ABS</Typography>}
                        </TableCell>
                        <TableCell sx={{ py: 1, width: 80 }}>
                          {g.not_elp != null ? (
                            <Chip
                              label={parseFloat(g.not_elp) >= 10 ? 'Validé' : 'Non validé'}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20, bgcolor: parseFloat(g.not_elp) >= 10 ? '#dcfce7' : '#fee2e2', color: parseFloat(g.not_elp) >= 10 ? '#16a34a' : '#dc2626' }}
                            />
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            );
          })}
        </Box>
      );
    };

    return (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ pb: 0, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Avatar src={adminAPI.getStudentPhotoUrl(student.cod_etu)} sx={{ width: 52, height: 52, bgcolor: '#2c3e50', fontSize: '1.4rem' }}>
              {student.lib_nom_pat_ind?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>{student.lib_nom_pat_ind} {student.lib_pr1_ind}</Typography>
              <Typography variant="caption" color="text.secondary">
                {student.cod_etu} · {student.lib_etp || '—'} · {student.cod_anu || '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.6 }}>
                <Chip
                  size="small"
                  label={student.is_blocked ? 'Compte bloqué' : 'Compte actif'}
                  color={student.is_blocked ? 'error' : 'success'}
                  variant={student.is_blocked ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  icon={<SecurityIcon sx={{ fontSize: 13 }} />}
                  label={student.totp_enabled ? '2FA activée' : '2FA désactivée'}
                  color={student.totp_enabled ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.68rem' }}
                />
                <Chip
                  size="small"
                  label={student.has_custom_password ? 'Mot de passe personnalisé' : 'Mot de passe par défaut'}
                  color={student.has_custom_password ? 'info' : 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.68rem' }}
                />
              </Box>
            </Box>
            <Button onClick={() => setDialogOpen(false)} size="small" variant="outlined" sx={{ borderRadius: 2, flexShrink: 0 }}>Fermer</Button>
          </Box>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42, fontSize: '0.82rem' } }}>
            <Tab icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Profil" />
            <Tab icon={<CalendarIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Situation Administrative" />
            <Tab icon={<MenuBookIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Situation Pédagogique" />
            <Tab icon={<AssessmentIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Notes & Résultats" />
          </Tabs>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, overflow: 'auto' }}>
          {tabValue === 0 && <ProfilTab />}
          {tabValue === 1 && <AdminSitTab />}
          {tabValue === 2 && <PedagoSitTab />}
          {tabValue === 3 && <NotesTab />}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" color="primary" gutterBottom>
          Student Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search and manage student records with comprehensive details
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="end">
            {/* Search Field */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Students"
                placeholder="Name, CIN, or Student Code"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            {/* Program Filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Program</InputLabel>
                <Select
                  value={filters.program}
                  onChange={(e) => handleFilterChange('program', e.target.value)}
                  label="Program"
                >
                  <MenuItem value="">All Programs</MenuItem>
                  {programs.map((program) => (
                    <MenuItem key={program} value={program}>
                      {program}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Year Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  label="Year"
                >
                  <MenuItem value="">All Years</MenuItem>
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </Button>
                
                {/* === NEW: Export Button === */}
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={isLoading}
                  title="Export current list to CSV"
                >
                  Export
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={isLoading}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => loadStudents()}
                  disabled={isLoading}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      {pagination.total_count > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
            {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
            {pagination.total_count} students
          </Typography>
          
          <Chip
            icon={<PeopleIcon />}
            label={`${pagination.total_count} Total Students`}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {/* Students Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : students.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No students found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell><strong>Student Code</strong></TableCell>
                    <TableCell><strong>Full Name</strong></TableCell>
                    <TableCell><strong>CIN</strong></TableCell>
                    <TableCell><strong>Program</strong></TableCell>
                    <TableCell><strong>Year</strong></TableCell>
                    <TableCell><strong>Sync Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow 
                      key={student.id}
                      sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary">
                          {student.cod_etu}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {student.full_name}
                          {student.is_blocked && (
                            <Tooltip title={student.block_message || 'Compte bloqué'}>
                              <Chip label="Bloqué" color="error" size="small" sx={{ fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{student.cin_ind}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                          {student.lib_etp}
                        </Typography>
                      </TableCell>
                      <TableCell>{student.cod_anu}</TableCell>
                      <TableCell>
                        <Chip
                          label={student.sync_status === 'recent' ? 'Recent' : 
                                student.sync_status === 'week' ? 'This Week' : 'Old'}
                          color={getSyncStatusColor(student.sync_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Voir le dossier">
                            <IconButton color="primary" onClick={() => viewStudentDetails(student.id)} size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Changer le mot de passe">
                            <IconButton color="warning" onClick={() => openPwdDialog(student)} size="small">
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          {student.totp_enabled && (
                            <Tooltip title="Désactiver la 2FA">
                              <IconButton color="error" size="small" onClick={() => setDisable2FADialog({ open: true, codEtu: student.cod_etu, name: student.full_name })}>
                                <Disable2FAIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={student.is_blocked ? 'Débloquer l\'accès' : 'Bloquer l\'accès'}>
                            <IconButton color={student.is_blocked ? 'success' : 'error'} size="small" onClick={() => openBlockDialog(student)}>
                              {student.is_blocked ? <UnblockIcon /> : <BlockIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Password Reset Dialog */}
      <Dialog open={pwdDialog.open} onClose={() => setPwdDialog({ open: false, codEtu: '', name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetIcon color="warning" />
          Changer le mot de passe
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Étudiant : <strong>{pwdDialog.name}</strong> ({pwdDialog.codEtu})
          </Typography>

          {pwdError  && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setPwdError(null)}>{pwdError}</Alert>}
          {pwdSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwdSuccess(null)}>{pwdSuccess}</Alert>}

          <TextField
            fullWidth
            label="Nouveau mot de passe"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder={`Laisser vide pour réinitialiser au code (${pwdDialog.codEtu})`}
            helperText="Minimum 6 caractères. Vide = réinitialiser au code étudiant par défaut."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdDialog({ open: false, codEtu: '', name: '' })}>Annuler</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={pwdLoading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
            onClick={handleResetPassword}
            disabled={pwdLoading}
          >
            {newPassword ? 'Enregistrer' : 'Réinitialiser par défaut'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Confirmation Dialog */}
      <Dialog open={disable2FADialog.open} onClose={() => setDisable2FADialog({ open: false, codEtu: '', name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Disable2FAIcon color="error" />
          Désactiver la double authentification
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Étudiant : <strong>{disable2FADialog.name}</strong> ({disable2FADialog.codEtu})
          </Typography>
          <Typography variant="body2">
            La 2FA sera immédiatement désactivée. L'étudiant pourra se reconnecter sans code à usage unique et devra reconfigurer la 2FA s'il le souhaite.
          </Typography>
          {disable2FAError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDisable2FAError(null)}>{disable2FAError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisable2FADialog({ open: false, codEtu: '', name: '' })}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={disable2FALoading ? <CircularProgress size={16} color="inherit" /> : <Disable2FAIcon />}
            onClick={handleDisable2FA}
            disabled={disable2FALoading}
          >
            Désactiver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block / Unblock Login Dialog */}
      <Dialog open={blockDialog.open} onClose={() => setBlockDialog({ open: false, codEtu: '', name: '', isBlocked: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {blockDialog.isBlocked ? <UnblockIcon color="success" /> : <BlockIcon color="error" />}
          {blockDialog.isBlocked ? 'Débloquer l\'accès' : 'Bloquer l\'accès'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Étudiant : <strong>{blockDialog.name}</strong> ({blockDialog.codEtu})
          </Typography>

          {blockDialog.isBlocked ? (
            <Typography variant="body2">
              L'étudiant pourra à nouveau se connecter normalement.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                L'étudiant ne pourra plus se connecter. Le message ci-dessous s'affichera à sa place lorsqu'il tentera de se connecter.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Message affiché à l'étudiant"
                value={blockMessage}
                onChange={e => setBlockMessage(e.target.value)}
                placeholder="Ex : Votre compte est suspendu. Merci de contacter le service de scolarité."
              />
            </>
          )}

          {blockError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setBlockError(null)}>{blockError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog({ open: false, codEtu: '', name: '', isBlocked: false })}>Annuler</Button>
          <Button
            variant="contained"
            color={blockDialog.isBlocked ? 'success' : 'error'}
            startIcon={blockLoading ? <CircularProgress size={16} color="inherit" /> : (blockDialog.isBlocked ? <UnblockIcon /> : <BlockIcon />)}
            onClick={handleBlockToggle}
            disabled={blockLoading}
          >
            {blockDialog.isBlocked ? 'Débloquer' : 'Bloquer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Student Details Dialog */}
      <StudentDetailsDialog />
    </Box>
  );
};

export default StudentManagement;